import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { ProductCombobox } from '@shared/components/ui/ProductCombobox';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Checkbox } from '@shared/components/ui/checkbox';
import type { ProductOption } from '@shared/components/ui/ProductCombobox';
import { RadioGroup, RadioGroupItem } from '@shared/components/ui/shadcn/radio-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faCheck, faXmark } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { UnlinkedProductsErrorApiAdapter } from '../../../adapters/UnlinkedProductsErrorApiAdapter';
import { ProductsApiAdapter } from '@modules/Products/CreateEdit/adapters/ProductsApiAdapter';
import { PackApiAdapter } from '@modules/Packs/adapters/PackApiAdapter';
import { translateOrderApiError } from '../../../../CreateEditOrders/utils/errorTranslator';

// Interfaces para los tipos de datos
interface OrderLine {
  id: number;
  product_id: number | null;
  pack_id: number | null;
  name: string;
  amount: number;
  qty: number;
  external_product_id?: string | null;
  external_variant_id?: string | null;
  product?: {
    id: number;
    name: string;
    code: string;
    image_hash: string;
  } | null;
  pack?: any | null;
}

interface SystemProduct {
  id: number;
  name: string;
  code: string;
  image_hash?: string; // 🆕 Campo de imagen del producto
}

interface SystemPack {
  id: number;
  title: string;
  team_id: number;
  versions: Array<{
    id: number;
    version: number;
    lines: Array<{
      product_id: number;
      quantity: number;
    }>;
  }>;
  liked_packs: any[];
  team: {
    id: number;
    name: string;
  };
}

interface UnlinkedProduct {
  id: number;
  name: string;
  type: 'product' | 'pack';
  selectedSystemItem: string;
  originalLine: OrderLine;
  saveLinkForFuture: boolean;
  applyToImportedOrders: boolean;
  unlinkedCount?: {
    orders_count: number;
    lines_count: number;
  };
}

interface OrderData {
  id: number;
  shop_id: number;
  lines: OrderLine[];
}

interface LinkResult {
  productName: string;
  success: boolean;
  message: string;
  type: 'external_link' | 'imported_orders';
}

export interface UnlinkedProductsErrorModalRef {
  openModal: (orderData: OrderData) => void;
  closeModal: () => void;
}

// Función para detectar nombres duplicados
const getDuplicateNames = (items: Array<{ name?: string; title?: string }>) => {
  const nameCounts: Record<string, number> = {};

  items.forEach((item) => {
    const name = item.name || item.title || '';
    nameCounts[name] = (nameCounts[name] || 0) + 1;
  });

  return Object.keys(nameCounts).filter((name) => nameCounts[name] > 1);
};

// Función para convertir productos del sistema a formato ProductOption
const formatSystemProducts = (products: SystemProduct[]): ProductOption[] => {
  const duplicateNames = getDuplicateNames(products);

  return products.map((product) => {
    const hasDuplicate = duplicateNames.includes(product.name);
    const name = hasDuplicate ? `${product.name} (ID: ${product.id})` : product.name;

    return {
      id: product.id,
      name: name,
      code: product.code,
      image: product.image_hash,
      value: product.id.toString(),
    };
  });
};

// Función para convertir packs del sistema a formato de opciones
const formatSystemPacks = (packs: SystemPack[]) => {
  const duplicateNames = getDuplicateNames(packs);

  return packs.map((pack) => {
    const hasDuplicate = duplicateNames.includes(pack.title);
    const label = hasDuplicate ? `${pack.title} (ID: ${pack.id})` : pack.title;

    return {
      label,
      value: pack.id.toString(),
    };
  });
};

interface UnlinkedProductsErrorModalProps {
  onSuccess?: () => void;
}

export const UnlinkedProductsErrorModal = forwardRef<UnlinkedProductsErrorModalRef, UnlinkedProductsErrorModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('viewOrdersModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const resultsModalRef = useRef<ModalCenterRef>(null);
    const [unlinkedProducts, setUnlinkedProducts] = useState<UnlinkedProduct[]>([]);
    const [systemProducts, setSystemProducts] = useState<SystemProduct[]>([]);
    const [systemPacks, setSystemPacks] = useState<SystemPack[]>([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [isLoadingPacks, setIsLoadingPacks] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentOrderId, setCurrentOrderId] = useState<number | null>(null);
    const [currentShopId, setCurrentShopId] = useState<number | null>(null);
    const [linkResults, setLinkResults] = useState<LinkResult[]>([]);

    // Función para cargar productos del sistema
    const fetchSystemProducts = async () => {
      setIsLoadingProducts(true);
      try {
        const httpClient = createHttpClient(apiClientWH);
        const response = await httpClient.get('/products', { fields: 'basic', per_page: 5000 });
        const responseData = (response as any).data;

        // Verificar si responseData es un array directamente o tiene una propiedad data
        const products = Array.isArray(responseData) ? responseData : responseData?.data || [];

        console.log('🔍 fetchSystemProducts - Response:', response);
        console.log('🔍 fetchSystemProducts - ResponseData:', responseData);
        console.log('🔍 fetchSystemProducts - Products:', products);

        setSystemProducts(products);
      } catch (error: any) {
        console.error('Error fetching system products:', error);
        toast({
          title: t('unlinkedProducts.fetchError.title'),
          description: error?.message || t('unlinkedProducts.fetchError.description'),
          variant: 'destructive',
        });
      } finally {
        setIsLoadingProducts(false);
      }
    };

    // Función para cargar packs del sistema
    const fetchSystemPacks = async () => {
      setIsLoadingPacks(true);
      try {
        const httpClient = createHttpClient(apiClientWH);
        const response = await httpClient.get('/packs?per_page=5000');
        const responseData = (response as any).data;

        // Verificar si responseData es un array directamente o tiene una propiedad data
        const packs = Array.isArray(responseData) ? responseData : responseData?.data || [];

        console.log('🔍 fetchSystemPacks - Response:', response);
        console.log('🔍 fetchSystemPacks - ResponseData:', responseData);
        console.log('🔍 fetchSystemPacks - Packs:', packs);

        setSystemPacks(packs);
      } catch (error: any) {
        console.error('Error fetching system packs:', error);
        toast({
          title: t('unlinkedProducts.fetchError.title'),
          description: error?.message || t('unlinkedProducts.fetchError.description'),
          variant: 'destructive',
        });
      } finally {
        setIsLoadingPacks(false);
      }
    };

    // Función para procesar las líneas del pedido y crear productos sin enlazar
    const processOrderLines = (lines: OrderLine[]) => {
      // Filtrar únicamente los lines que tengan product_id: null y pack_id: null
      const unlinkedLines = lines.filter((line) => line.product_id === null && line.pack_id === null);

      const unlinkedProducts: UnlinkedProduct[] = unlinkedLines.map((line) => ({
        id: line.id,
        name: line.name,
        type: 'product' as 'product' | 'pack',
        selectedSystemItem: '',
        originalLine: line,
        saveLinkForFuture: false,
        applyToImportedOrders: false,
      }));

      setUnlinkedProducts(unlinkedProducts);
    };

    // Función para cargar el conteo de líneas sin vincular para un producto específico
    const fetchUnlinkedLinesCount = async (productId: number, variantId: string) => {
      if (!currentShopId || !variantId) return;

      try {
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new UnlinkedProductsErrorApiAdapter(httpClient);
        const response = await adapter.getUnlinkedLinesCount(currentShopId, variantId);

        setUnlinkedProducts((prev) =>
          prev.map((product) =>
            product.id === productId
              ? {
                  ...product,
                  unlinkedCount: {
                    orders_count: response.orders_count,
                    lines_count: response.lines_count,
                  },
                }
              : product,
          ),
        );
      } catch (error) {
        console.error('Error fetching unlinked lines count:', error);
      }
    };

    // Handlers para los checkboxes
    const handleSaveLinkForFutureChange = (productId: number, checked: boolean) => {
      setUnlinkedProducts((prev) =>
        prev.map((product) => (product.id === productId ? { ...product, saveLinkForFuture: checked } : product)),
      );
    };

    const handleApplyToImportedOrdersChange = (productId: number, checked: boolean) => {
      setUnlinkedProducts((prev) =>
        prev.map((product) => (product.id === productId ? { ...product, applyToImportedOrders: checked } : product)),
      );
    };

    useImperativeHandle(ref, () => ({
      openModal: (order: OrderData) => {
        // Validar que order y order.lines existan
        if (!order || !order.lines || !Array.isArray(order.lines)) {
          console.error('Invalid order data provided to UnlinkedProductsErrorModal');
          toast({
            title: t('unlinkedProducts.invalidData.title'),
            description: t('unlinkedProducts.invalidData.description'),
            variant: 'destructive',
          });
          return;
        }

        // Guardar datos del pedido
        setCurrentOrderId(order.id);
        setCurrentShopId(order.shop_id);
        setLinkResults([]);

        // Procesar las líneas del pedido
        processOrderLines(order.lines);

        // Cargar productos y packs del sistema
        fetchSystemProducts();
        fetchSystemPacks();

        modalRef.current?.openModal();
      },
      closeModal: () => modalRef.current?.closeModal(),
    }));

    const handleClose = () => {
      modalRef.current?.closeModal();
    };

    const handleBack = () => {
      modalRef.current?.closeModal();
    };

    const handleAccept = async () => {
      if (!currentOrderId) {
        toast({
          title: t('unlinkedProducts.error.title'),
          description: 'ID del pedido no encontrado',
          variant: 'destructive',
        });
        return;
      }

      // Validar que todos los productos tengan selección
      const unlinkedProductsWithSelection = unlinkedProducts.filter((product) => product.selectedSystemItem);

      if (unlinkedProductsWithSelection.length !== unlinkedProducts.length) {
        toast({
          title: t('unlinkedProducts.error.title'),
          description: 'Debes seleccionar un producto o pack para todos los elementos',
          variant: 'destructive',
        });
        return;
      }

      setIsSubmitting(true);
      const results: LinkResult[] = [];

      try {
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new UnlinkedProductsErrorApiAdapter(httpClient);
        const productAdapter = new ProductsApiAdapter(httpClient);
        const packAdapter = new PackApiAdapter(httpClient);

        // Preparar datos para el API
        const lines = unlinkedProducts.map((product) => {
          const selectedItemId = parseInt(product.selectedSystemItem);
          return {
            id: product.id,
            product_id: product.type === 'product' ? selectedItemId : null,
            pack_id: product.type === 'pack' ? selectedItemId : null,
            save_link_for_future: product.saveLinkForFuture,
            apply_to_imported_orders: product.applyToImportedOrders,
          };
        });

        console.log('🔍 UnlinkedProductsErrorModal - Sending data:', {
          orderId: currentOrderId,
          lines,
        });

        const response = await adapter.solveUnlinkedProductsError(currentOrderId, { lines });

        console.log('🔍 UnlinkedProductsErrorModal - Response:', response);

        if (response.success) {
          // Procesar vinculaciones externas para productos con saveLinkForFuture
          const itemsToLink = unlinkedProducts.filter(
            (product) =>
              product.saveLinkForFuture &&
              product.originalLine.external_product_id &&
              product.originalLine.external_variant_id &&
              currentShopId,
          );

          for (const item of itemsToLink) {
            try {
              if (item.type === 'product') {
                await productAdapter.createLink({
                  productId: parseInt(item.selectedSystemItem),
                  externalProductId: item.originalLine.external_product_id!,
                  externalVariantId: item.originalLine.external_variant_id!,
                  shopId: currentShopId!,
                });
              } else {
                await packAdapter.createLink({
                  pack_id: parseInt(item.selectedSystemItem),
                  external_product_id: parseInt(item.originalLine.external_product_id!),
                  external_variant_id: parseInt(item.originalLine.external_variant_id!),
                  shop_id: currentShopId!,
                });
              }

              results.push({
                productName: item.name,
                success: true,
                message: t('unlinkedProducts.linkResults.success'),
                type: 'external_link',
              });
            } catch (error: any) {
              const errorMessage = error?.message || '';
              const isDuplicateLinkError =
                errorMessage.toLowerCase().includes('ya está vinculad') ||
                errorMessage.toLowerCase().includes('already linked') ||
                errorMessage.toLowerCase().includes('duplicate');

              results.push({
                productName: item.name,
                success: false,
                message: isDuplicateLinkError
                  ? t('unlinkedProducts.linkResults.duplicate')
                  : t('unlinkedProducts.linkResults.error'),
                type: 'external_link',
              });
            }
          }

          // Procesar vinculación de pedidos importados para productos con applyToImportedOrders
          const itemsToLinkImported = unlinkedProducts.filter(
            (product) => product.applyToImportedOrders && product.originalLine.external_variant_id && currentShopId,
          );

          for (const item of itemsToLinkImported) {
            try {
              const linkResponse = await adapter.linkOrderLines({
                shopId: currentShopId!,
                variantId: item.originalLine.external_variant_id!,
                productId: item.type === 'product' ? parseInt(item.selectedSystemItem) : undefined,
                packId: item.type === 'pack' ? parseInt(item.selectedSystemItem) : undefined,
              });

              results.push({
                productName: item.name,
                success: true,
                message: t('unlinkedProducts.linkResults.ordersLinked', {
                  count: linkResponse.updated_lines,
                }),
                type: 'imported_orders',
              });
            } catch (error: any) {
              results.push({
                productName: item.name,
                success: false,
                message: error?.message || t('unlinkedProducts.linkResults.error'),
                type: 'imported_orders',
              });
            }
          }

          // Si hay resultados de vinculaciones, mostrar modal de resultados
          if (results.length > 0) {
            setLinkResults(results);
            modalRef.current?.closeModal();
            setTimeout(() => {
              resultsModalRef.current?.openModal();
            }, 300);
          } else {
            toast({
              title: t('unlinkedProducts.success.title'),
              description: response.message,
              variant: 'default',
            });
            modalRef.current?.closeModal();
            onSuccess?.();
          }
        } else {
          toast({
            title: t('unlinkedProducts.error.title'),
            description: response.message,
            variant: 'destructive',
          });
        }
      } catch (error: any) {
        // Traducir el error usando el sistema de traducción
        const translatedError = translateOrderApiError(error, t);

        toast({
          title: t('unlinkedProducts.error.title'),
          description: translatedError,
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleResultsModalClose = () => {
      resultsModalRef.current?.closeModal();
      onSuccess?.();
    };

    const handleTypeChange = (productId: number, type: 'product' | 'pack') => {
      setUnlinkedProducts((prev) =>
        prev.map((product) => (product.id === productId ? { ...product, type, selectedSystemItem: '' } : product)),
      );
    };

    const handleSystemItemChange = (productId: number, value: string) => {
      setUnlinkedProducts((prev) =>
        prev.map((product) => (product.id === productId ? { ...product, selectedSystemItem: value } : product)),
      );

      // Buscar el producto para obtener el external_variant_id
      const product = unlinkedProducts.find((p) => p.id === productId);
      if (product?.originalLine.external_variant_id && currentShopId && value) {
        fetchUnlinkedLinesCount(productId, product.originalLine.external_variant_id);
      }
    };

    return (
      <>
        <ModalCenter
          ref={modalRef}
          title={t('unlinkedProducts.title')}
          icon={
            <button
              onClick={handleBack}
              className="flex items-center justify-center w-6 h-6 hover:bg-hover-press rounded transition-colors"
              data-testid="back-button"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
            </button>
          }
          type="default"
          width="lg"
          className="max-h-[90vh]"
        >
          <div className="space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Texto explicativo */}
            <p className="text-sm">{t('unlinkedProducts.description')}</p>

            {/* Lista de productos sin enlazar */}
            <div className="space-y-4">
              {unlinkedProducts.map((product) => (
                <SectionBox key={product.id}>
                  {/* Nombre del producto */}
                  <h3 className="font-medium text-title">{product.name}</h3>

                  {/* Opciones de enlace */}
                  <div className="space-y-3 flex gap-5 items-center">
                    <div>
                      <p className="text-sm mb-3">{t('unlinkedProducts.linkTo')}</p>

                      {/* Radio buttons para tipo */}
                      <RadioGroup
                        value={product.type}
                        onValueChange={(value) => handleTypeChange(product.id, value as 'product' | 'pack')}
                        className="flex gap-4"
                        data-testid={`type-radio-group-${product.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="product" id={`product-${product.id}`} />
                          <label htmlFor={`product-${product.id}`} className="text-sm text-title cursor-pointer">
                            {t('unlinkedProducts.product')}
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="pack" id={`pack-${product.id}`} />
                          <label htmlFor={`pack-${product.id}`} className="text-sm text-title cursor-pointer">
                            {t('unlinkedProducts.pack')}
                          </label>
                        </div>
                      </RadioGroup>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm mb-3">
                        {product.type === 'product'
                          ? t('unlinkedProducts.systemProduct')
                          : t('unlinkedProducts.systemPack')}
                      </p>
                      {/* Dropdown para seleccionar producto/pack del sistema */}
                      <div className="w-full">
                        {(() => {
                          const options =
                            product.type === 'product'
                              ? formatSystemProducts(systemProducts)
                              : formatSystemPacks(systemPacks);

                          console.log(`🔍 ${product.type} options for product ${product.id}:`, options);
                          console.log(
                            `🔍 system${product.type === 'product' ? 'Products' : 'Packs'}:`,
                            product.type === 'product' ? systemProducts : systemPacks,
                          );

                          // 🎯 Usar ProductCombobox para productos (con imagen) y Combobox para packs
                          return product.type === 'product' ? (
                            <ProductCombobox
                              value={product.selectedSystemItem}
                              setValue={(value) => handleSystemItemChange(product.id, value)}
                              placeholder={
                                isLoadingProducts
                                  ? t('unlinkedProducts.loadingProducts')
                                  : t('unlinkedProducts.selectSystemProduct')
                              }
                              searchFn={async (query: string) => {
                                // Función de búsqueda local en las opciones ya cargadas
                                const filtered = (options as ProductOption[]).filter((opt) => {
                                  const q = query.toLowerCase();
                                  return opt.name.toLowerCase().includes(q) || opt.code?.toLowerCase().includes(q);
                                });
                                return Promise.resolve(filtered);
                              }}
                              dataTestId={`system-product-select-${product.id}`}
                              disabled={isLoadingProducts}
                              loadingMessage={t('unlinkedProducts.loadingProducts')}
                              emptyMessage={t('unlinkedProducts.noProductsFound')}
                            />
                          ) : (
                            <Combobox
                              options={options}
                              value={product.selectedSystemItem}
                              setValue={(value) => handleSystemItemChange(product.id, value)}
                              placeholder={
                                isLoadingPacks
                                  ? t('unlinkedProducts.loadingPacks')
                                  : t('unlinkedProducts.selectSystemPack')
                              }
                              dataTestId={`system-pack-select-${product.id}`}
                              disabled={isLoadingPacks}
                            />
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Checkboxes de vinculación - Solo se muestran cuando hay datos externos completos */}
                  {product.selectedSystemItem &&
                    product.originalLine.external_product_id &&
                    product.originalLine.external_variant_id &&
                    currentShopId && (
                      <div className="mt-4 space-y-3 border-t border-border pt-4">
                        {/* Checkbox 1: Guardar vinculación para futuros pedidos */}
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`save-link-${product.id}`}
                            checked={product.saveLinkForFuture}
                            onCheckedChange={(checked: boolean) => handleSaveLinkForFutureChange(product.id, checked)}
                            data-testid={`save-link-checkbox-${product.id}`}
                          />
                          <label htmlFor={`save-link-${product.id}`} className="text-sm text-title cursor-pointer">
                            {t('unlinkedProducts.saveLinkForFuture')}
                          </label>
                        </div>

                        {/* Checkbox 2: Aplicar a pedidos importados */}
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`apply-imported-${product.id}`}
                            checked={product.applyToImportedOrders}
                            onCheckedChange={(checked: boolean) =>
                              handleApplyToImportedOrdersChange(product.id, checked)
                            }
                            data-testid={`apply-imported-checkbox-${product.id}`}
                          />
                          <label htmlFor={`apply-imported-${product.id}`} className="text-sm text-title cursor-pointer">
                            {t('unlinkedProducts.applyToImportedOrders')}
                            {product.unlinkedCount && product.unlinkedCount.orders_count > 0 && (
                              <span className="ml-2 text-complementary-text">
                                (
                                {t('unlinkedProducts.unlinkedCount', {
                                  orders: product.unlinkedCount.orders_count,
                                })}
                                )
                              </span>
                            )}
                          </label>
                        </div>
                      </div>
                    )}
                </SectionBox>
              ))}
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleClose} data-testid="cancel-button" size="lg">
                {t('unlinkedProducts.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleAccept}
                data-testid="accept-button"
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('unlinkedProducts.submitting') : t('unlinkedProducts.accept')}
              </Button>
            </div>
          </div>
        </ModalCenter>

        {/* Modal de resultados de vinculación */}
        <ModalCenter ref={resultsModalRef} title={t('unlinkedProducts.linkResults.title')} type="default" width="lg">
          <div className="space-y-4">
            {linkResults.map((result, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border ${
                  result.success ? 'bg-success/10 border-success/20' : 'bg-error/10 border-error/20'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                      result.success ? 'bg-success' : 'bg-error'
                    }`}
                  >
                    <FontAwesomeIcon icon={result.success ? faCheck : faXmark} className="w-3 h-3 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-title">{result.productName}</p>
                    <p className={`text-sm ${result.success ? 'text-success' : 'text-error'}`}>{result.message}</p>
                    <p className="text-xs text-complementary-text mt-1">
                      {result.type === 'external_link'
                        ? t('unlinkedProducts.linkResults.typeExternalLink')
                        : t('unlinkedProducts.linkResults.typeImportedOrders')}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-end pt-4">
              <Button variant="primary" onClick={handleResultsModalClose} data-testid="results-close-button" size="lg">
                {t('unlinkedProducts.linkResults.close')}
              </Button>
            </div>
          </div>
        </ModalCenter>
      </>
    );
  },
);
